import React from 'react';
import Link from 'next/link';
import TrackedLink from './TrackedLink';
import type { EventName, TrackProps } from '@/utils/analytics-events';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet';

const BASE =
  'inline-flex items-center justify-center rounded-control font-semibold text-center transition ' +
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep';

const VARIANT: Record<ButtonVariant, string> = {
  // Marigold with a 2px deep bottom edge that lifts on hover and presses on active.
  primary:
    'bg-marigold text-marigold-ink border-b-2 border-marigold-deep hover:-translate-y-px active:translate-y-0 active:border-b',
  secondary: 'border-2 border-ink text-ink bg-transparent hover:bg-paper-raised',
  quiet: 'text-ink underline underline-offset-4 decoration-marigold-deep hover:text-ink-soft',
};

const SIZE = { md: 'min-h-[44px] px-5 text-base', lg: 'min-h-[52px] px-7 text-lg' } as const;

/** Server-safe link styled as a button. With `event`, it renders the client TrackedLink. */
export function ButtonLink({ href, variant = 'primary', size = 'md', event, children, className = '' }: {
  href: string;
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  event?: { name: EventName; props?: TrackProps };
  children: React.ReactNode;
  className?: string;
}) {
  const cls = `${BASE} ${VARIANT[variant]} ${SIZE[size]} ${className}`.trim();
  if (event) {
    return <TrackedLink href={href} event={event.name} props={event.props} className={cls}>{children}</TrackedLink>;
  }
  return <Link href={href} className={cls}>{children}</Link>;
}

export default ButtonLink;
